import { Stack, Typography } from '@mui/material';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BETTER_AUTH_BASE_PATH } from '@/libs/better-auth-client';
import { clearRedirectCookie, toAbsoluteAppUrl } from './auth-utils';
import AuthSplitLayout from './AuthSplitLayout';

/**
 * Magic-link landing page. The transactional email links here (frontend origin) rather than straight
 * to the backend, so the user lands on the web app. Better Auth has no client-side verify action for
 * magic links — verification is a top-level `GET /auth/magic-link/verify` navigation that consumes the
 * token, sets the session cookie, and 302-redirects to `callbackURL`. So this page simply forwards the
 * token to that endpoint with an absolute frontend `callbackURL` (defaulting to the app home) so the
 * session lands the user back in the web app. Must be a PUBLIC route: the visitor is unauthenticated,
 * and a private route would trip the session query → Apollo bounces to `/auth/login?redirectTo=…`.
 */
export default function AuthMagicLink() {
    const { t } = useTranslation();

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const token = params.get('token');
        if (!token) {
            window.location.replace('/auth/login');
            return;
        }

        // Land on the app after verification. Only honour the link's own `callbackURL` if it's a safe
        // same-origin app path — never an `/auth/*` page (that would bounce back here and loop the
        // verify), and deliberately NOT getRedirectPath()/the REDIRECT_AFTER_LOGIN cookie: the pre-fix
        // Apollo login-bounce stored this very magic-link URL there, which is what caused the loop.
        clearRedirectCookie();
        const rawCallback = params.get('callbackURL') || '';
        const isSafeAppPath = /^\/(?!\/)/.test(rawCallback) && !rawCallback.startsWith('/auth/');
        const callbackURL = toAbsoluteAppUrl(isSafeAppPath ? rawCallback : '/home');
        const errorCallbackURL = toAbsoluteAppUrl('/auth/login');

        const verifyUrl =
            `${import.meta.env.VITE_API_ROOT}${BETTER_AUTH_BASE_PATH}/magic-link/verify` +
            `?token=${encodeURIComponent(token)}` +
            `&callbackURL=${encodeURIComponent(callbackURL)}` +
            `&errorCallbackURL=${encodeURIComponent(errorCallbackURL)}`;

        window.location.replace(verifyUrl);
    }, []);

    return (
        <AuthSplitLayout>
            <Stack alignItems="center" justifyContent="center" minHeight={200}>
                <Typography>{t('auth.common.loading')}</Typography>
            </Stack>
        </AuthSplitLayout>
    );
}
